import React, { useState, useEffect, useRef } from 'react';
import { Button, Tag } from 'antd';
import { LikeFilled, LikeOutlined, DislikeFilled, DislikeOutlined, CheckCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BaseText } from 'src/components/typography';
import { ColorMappingClassification, PredictedLabel } from 'src/interfaces/open-ai-interface';
import './AIConversationView.scss';

interface Message {
  id?: string;
  role: string;
  content: string;
  createdAt: string;
  thumbsUp?: boolean | null;
  softDelete?: boolean;
  classification?: string;
}

interface AIConversationViewProps {
  conversationData: Message[];
  clientName?: string;
  onThumbsUp?: (index: number) => void;
  onThumbsDown?: (index: number) => void;
  onSubmitRefinedResponse?: (index: number, feedback: string) => void;
  showActions?: boolean;
  conversationDate?: string;
  highlightedMessageId?: string | null;
  highlightedClassification?: string | null;
}

const AIConversationView: React.FC<AIConversationViewProps> = ({
  conversationData,
  clientName = 'Client',
  onThumbsUp,
  onThumbsDown,
  onSubmitRefinedResponse,
  showActions = true,
  conversationDate,
  highlightedMessageId = null,
  highlightedClassification = null
}) => {
  const [refinedInputIndex, setRefinedInputIndex] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState<boolean[]>([]);
  const highlightedMessageRef = useRef<HTMLDivElement>(null);

  console.log(conversationData);
  // Scroll to highlighted message when component mounts or message changes
  useEffect(() => {
    if (highlightedMessageId && highlightedMessageRef.current) {
      setTimeout(() => {
        highlightedMessageRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 500); // Small delay to ensure component is fully rendered
    }
  }, [highlightedMessageId, conversationData]);

  if (!conversationData || conversationData.length === 0) {
    return <div className="AIConversationView__empty">No AI Conversation Data Available</div>;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const isSameDay = (date1: string, date2: string) => {
    return new Date(date1).toDateString() === new Date(date2).toDateString();
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Helper function to get classification styling using the existing system
  const getClassificationStyle = (classification: string) => {
    // Try to match the classification to existing PredictedLabel enum values
    const predLabel = Object.values(PredictedLabel).find(
      label => label.toLowerCase() === classification.toLowerCase()
    ) as PredictedLabel;
    
    if (predLabel && ColorMappingClassification[predLabel]) {
      const colors = ColorMappingClassification[predLabel];
      return {
        backgroundColor: colors.backgroundColor,
        color: colors.fontColor,
        severity: getSeverityLevel(predLabel)
      };
    }
    
    // Fallback for custom classifications not in the enum
    return {
      backgroundColor: 'rgba(24, 144, 255, 0.1)',
      color: '#1890ff',
      severity: 'info' as const
    };
  };

  // Helper function to determine severity level and icon
  const getSeverityLevel = (label: PredictedLabel) => {
    switch (label) {
      case PredictedLabel.ImmediateDanger:
        return { level: 'critical' as const, icon: ExclamationCircleOutlined };
      case PredictedLabel.SevereDistress:
        return { level: 'high' as const, icon: ExclamationCircleOutlined };
      case PredictedLabel.ModerateDistress:
        return { level: 'medium' as const, icon: WarningOutlined };
      case PredictedLabel.MildDistress:
      case PredictedLabel.InsensitiveDisrespectful:
        return { level: 'low' as const, icon: WarningOutlined };
      default:
        return { level: 'info' as const, icon: WarningOutlined };
    }
  };

  // Helper function to render classification badge
  const renderClassificationBadge = (message: Message) => {
    if (!message.classification || message.role !== 'user') return null;
    
    const style = getClassificationStyle(message.classification);
    const { icon: IconComponent } = getSeverityLevel(message.classification as PredictedLabel);
    
    return (
      <div className="AIConversationView__classification-badge">
        <div 
          className="AIConversationView__classification-tag"
          style={{
            backgroundColor: style.backgroundColor,
            color: style.color,
            border: `1px solid ${style.color}`
          }}
        >
          <IconComponent style={{ fontSize: '10px' }} />
          {message.classification}
        </div>
      </div>
    );
  };

  const handleThumbsUpClick = (index: number) => {
    if (onThumbsUp) {
      onThumbsUp(index);
      
      if (refinedInputIndex === index) {
        setRefinedInputIndex(null);
      }
    }
  };

  const handleThumbsDownClick = (index: number) => {
    if (onThumbsDown) {
      onThumbsDown(index);
      setRefinedInputIndex(index);
    }
  };

  const handleRefinedResponseSubmit = (index: number, additionalComments: string) => {
    if (onSubmitRefinedResponse) {
      onSubmitRefinedResponse(index, additionalComments);
    }

    const newSubmitted = [...submitted];
    newSubmitted[index] = true;
    setSubmitted(newSubmitted);
    setRefinedInputIndex(null);
  };

  const renderThumbsControls = (index: number, thumbsUp: boolean | null | undefined) => {
    if (!showActions) return null;
    
    return (
      <div className="AIConversationView__thumbs">
        <Button
          type="link"
          icon={
            thumbsUp === true ? (
              <LikeFilled style={{ color: '#48abe2' }} />
            ) : (
              <LikeOutlined style={{ color: 'gray' }} />
            )
          }
          onClick={() => handleThumbsUpClick(index)}
        />
        <Button
          type="link"
          icon={
            thumbsUp === false ? (
              <DislikeFilled style={{ color: '#ff7777' }} />
            ) : (
              <DislikeOutlined style={{ color: 'gray' }} />
            )
          }
          onClick={() => handleThumbsDownClick(index)}
        />
      </div>
    );
  };

  const renderAdditionalInputOrThankYou = (index: number) => {
    if (!showActions) return null;
    
    if (submitted[index]) {
      return <div className="AIConversationView__feedback-thanks">Thank you for your feedback!</div>;
    } else if (refinedInputIndex === index) {
      return (
        <div className="AIConversationView__feedback">
          <textarea
            id={`textarea-${index}`}
            className="AIConversationView__feedback-textarea"
            placeholder="Please provide a refined response..."
          />
          <div className="AIConversationView__feedback-actions">
            <button
              className="AIConversationView__feedback-submit"
              onClick={() => {
                const textarea = document.getElementById(
                  `textarea-${index}`,
                ) as HTMLTextAreaElement;
                const additionalComments = textarea ? textarea.value : '';
                handleRefinedResponseSubmit(index, additionalComments);
              }}
            >
              <CheckCircleOutlined /> Submit
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  let previousDate: string | null = null;

  return (
    <div className="AIConversationView">
      {/* Conversation section */}
      <div className="AIConversationView__chat">
        {conversationDate && (
          <div className="AIConversationView__chat-metadata">
            <BaseText type="caption">
              Started on {new Date(conversationDate).toLocaleString()}
            </BaseText>
          </div>
        )}
        
        <div className="AIConversationView__chat-messages">
          {conversationData.map((message, index) => {
            const showDateSeparator = !previousDate || !isSameDay(
              previousDate,
              message.createdAt,
            );
            previousDate = message.createdAt;
            
            // Check if this message should be highlighted
            const isHighlighted = highlightedMessageId && message.id === highlightedMessageId;
            // Check if this message has any classification (flagged)
            const isFlagged = message.classification && message.role === 'user';
            
            // Get severity level for flagged messages
            const severityLevel = isFlagged ? getSeverityLevel(message.classification as PredictedLabel).level : null;

            return (
              <div key={message.id || index}>
                {showDateSeparator && (
                  <div className="AIConversationView__date-separator">
                    <span>
                      {isToday(message.createdAt)
                        ? 'Today'
                        : formatDate(message.createdAt)}
                    </span>
                    <hr />
                  </div>
                )}
                <div
                  className={`AIConversationView__message AIConversationView__message--${message.role} ${message.softDelete ? 'AIConversationView__message--softDeleted softDeleted' : ''} ${isHighlighted ? 'AIConversationView__message--highlighted' : ''} ${isFlagged ? 'AIConversationView__message--flagged' : ''}`}
                  ref={isHighlighted ? highlightedMessageRef : null}
                  data-severity={severityLevel}
                >
                  {isHighlighted && (
                    <div className="AIConversationView__message-highlight-banner">
                      <WarningOutlined /> This message has been flagged as {highlightedClassification || message.classification || 'concerning'}
                    </div>
                  )}
                  <div className="AIConversationView__message-header">
                    <strong>{message.role === 'user' ? `${clientName}` : 'jAImee'}</strong>
                    <span className="AIConversationView__message-time">
                      {message.createdAt ? new Date(message.createdAt).toLocaleTimeString() : ''}
                      {message.softDelete && <span className="AIConversationView__message-deleted-tag"> (Deleted)</span>}
                    </span>
                  </div>
                  {/* Render classification badge for all flagged user messages */}
                  {renderClassificationBadge(message)}
                  <div className="AIConversationView__message-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code: ({node, inline, ...props}) => (
                          <code className="AIConversationView__message-code" {...props} />
                        ),
                        pre: ({node, ...props}) => (
                          <pre className="AIConversationView__message-pre" {...props} />
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                  {message.role === 'assistant' && renderThumbsControls(index, message.thumbsUp)}
                  {renderAdditionalInputOrThankYou(index)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIConversationView; 